/**
 * Mapeo campo_key (config campos obligatorios) → slug de GET /auxiliares/:slug
 * No incluir: provincia, poblacion, agente, tipo_via, tipo_inmueble, operacion
 * (tienen UI específica en los modales).
 */
export const AUX_SLUG_BY_CAMPO_KEY = {
  suelos: 'suelos',
  suelos_id: 'suelos',
  cocina: 'cocina',
  cocina_id: 'cocina',
  vistas: 'vistas',
  vistas_id: 'vistas',
  ub_vistas_id: 'vistas',
  calefaccion: 'calefaccion',
  calefaccion_id: 'calefaccion',
  eq_calefaccion: 'calefaccion',
  eq_calefaccion_id: 'calefaccion',
  orientacion: 'orientacion',
  orientacion_id: 'orientacion',
  eq_aire: 'aire-acondicionado',
  eq_aire_id: 'aire-acondicionado',
  aire_acondicionado_id: 'aire-acondicionado',
  muebles: 'muebles',
  muebles_id: 'muebles',
  piscina: 'piscina',
  piscina_id: 'piscina',
  eq_piscina_priv_id: 'piscina',
  eq_piscina_comun_id: 'piscina',
  tipo_terreno: 'tipo-terreno',
  tipo_terreno_id: 'tipo-terreno',
  zona: 'zona',
  zona_id: 'zona',
  urbanizacion: 'urbanizacion',
  urbanizacion_id: 'urbanizacion',
  etiqueta: 'etiqueta',
  etiqueta_id: 'etiqueta',
  estado_inmueble: 'estado-inmueble',
  estado_inmueble_id: 'estado-inmueble',
  carpinteria_int: 'carpinteria-interior',
  carpinteria_int_id: 'carpinteria-interior',
  carpinteria_interior_id: 'carpinteria-interior',
  carpinteria_ext: 'carpinteria-exterior',
  carpinteria_ext_id: 'carpinteria-exterior',
  carpinteria_exterior_id: 'carpinteria-exterior',
  procedencia: 'procedencias',
  procedencia_id: 'procedencias',
  fuente: 'procedencias',
  fuente_id: 'procedencias',
};

export function getAuxSlugForCampoKey(campoKey) {
  if (!campoKey) return null;
  return AUX_SLUG_BY_CAMPO_KEY[String(campoKey).toLowerCase()] || null;
}

export function mergeAuxSlugsNeededFromKeys(campoKeys) {
  const slugs = new Set();
  for (const k of campoKeys) {
    const s = getAuxSlugForCampoKey(k);
    if (s) slugs.add(s);
  }
  return [...slugs];
}

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Valor del <select>: id si coincide, o id por nombre legado */
export function normalizeAuxSelectValue(val, rows = []) {
  if (val === '' || val === null || val === undefined) return '';
  const s = String(val);
  if (rows.some((r) => String(r.id) === s)) return s;
  if (UUID_RX.test(s)) return s;
  const lower = s.toLowerCase();
  const nombreAlias = { parket: 'parquet', parke: 'parquet' };
  const altNombre = nombreAlias[lower];
  const row = rows.find((r) => {
    const n = String(r.nombre || '').toLowerCase();
    return n === lower || (altNombre && n === altNombre);
  });
  return row ? String(row.id) : '';
}

/** «No tiene» primero (campo obligatorio); tipos reales después. Meta «Sí» no se lista. */
export function ordenarOpcionesTipoEquipoDesplegable(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const reales = [];
  const ausentes = [];
  for (const r of list) {
    const nom = String(r?.nombre || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .trim();
    if (!nom) continue;
    if (/^(si|sí|tiene|con|yes)$/.test(nom)) continue; // meta, no listar
    if (
      /^(no tiene|ninguno|ninguna|sin|n\/a|-)$/.test(nom)
      || /^no tiene\b/.test(nom)
      || /^sin\s+(aire|aa|a\.?\s*a\.?|calefaccion|calef\.?)/.test(nom)
    ) {
      ausentes.push(r);
      continue;
    }
    reales.push(r);
  }
  if (!ausentes.length) return reales;
  // Un solo «No tiene» al principio (como estaba)
  const canon = { ...ausentes[0], nombre: 'No tiene' };
  return [canon, ...reales];
}

/** @deprecated usar ordenarOpcionesTipoEquipoDesplegable (conserva No tiene). */
export function filtrarOpcionesTipoEquipoReal(rows = []) {
  return ordenarOpcionesTipoEquipoDesplegable(rows);
}

/** Claves cuyo padre es booleano CRM (no UUID). El tipo va solo en *_id. */
const AUX_PADRE_BOOL = new Set([
  'eq_aire', 'eq_aire_id',
  'eq_calefaccion', 'eq_calefaccion_id',
  'eq_piscina_priv', 'eq_piscina_priv_id',
  'eq_piscina_comun', 'eq_piscina_comun_id',
]);

/**
 * Guarda UUID en *_id. En campos aux genéricos también en la clave base
 * (suelos/suelos_id). En clima/piscina el padre es boolean — no meter el UUID ahí.
 */
export function buildAuxExtrasPatch(campoKey, selectedId) {
  const k = String(campoKey);
  const id = selectedId || '';
  const patch = {};
  if (AUX_PADRE_BOOL.has(k)) {
    const padre = k.endsWith('_id') ? k.slice(0, -3) : k;
    const idK = k.endsWith('_id') ? k : `${k}_id`;
    patch[idK] = id;
    // El caller (modal) pone el boolean; si vacían el select, dejar false aquí
    if (!id) patch[padre] = false;
    return patch;
  }
  if (k.endsWith('_id')) {
    patch[k] = id;
    const base = k.slice(0, -3);
    if (base) patch[base] = id;
  } else {
    patch[k] = id;
    patch[`${k}_id`] = id;
  }
  return patch;
}

export const CAMPOS_VISTAS_MULTI = new Set(["vistas", "vistas_id", "ub_vistas_id"]);

export function normalizeVistasIdsFromExtras(extras = {}) {
  const raw = extras.ub_vistas_ids;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((x) => String(x)).filter(Boolean);
  }
  const single = extras.ub_vistas_id ?? extras.vistas_id ?? extras.vistas;
  return single ? [String(single)] : [];
}

export function buildVistasExtrasPatch(selectedIds = []) {
  const ids = (selectedIds || []).map((x) => String(x)).filter(Boolean);
  const patch = { ub_vistas_ids: ids };
  if (ids.length > 0) {
    patch.ub_vistas_id = ids[0];
    patch.vistas_id = ids[0];
    patch.vistas = ids[0];
  } else {
    patch.ub_vistas_id = "";
    patch.vistas_id = "";
    patch.vistas = "";
  }
  return patch;
}

export function isVistasMultiCampo(campoKey) {
  return CAMPOS_VISTAS_MULTI.has(String(campoKey || "").toLowerCase());
}
